import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Disc3, Users, Archive as ArchiveIcon, Building2, PoundSterling, Eye, EyeOff, FileBarChart2 } from "lucide-react";
import { formatMoney, formatMoneyWhole } from "@/lib/api";

const TOTAL_VALUE_HIDDEN_KEY = "wax_total_value_hidden";
const LOCATION_ORDER = ["Ruth", "Andy", "Archive", "Lagom"];
const LOCATION_CHIP_COLORS = {
    Ruth: "bg-[#C05A35]/10 text-[#C05A35] border-[#C05A35]/30",
    Andy: "bg-[#4A5D4E]/10 text-[#4A5D4E] border-[#4A5D4E]/30",
    Archive: "bg-foreground/5 text-foreground/70 border-foreground/20",
    Lagom: "bg-[#5A7D8C]/10 text-[#5A7D8C] border-[#5A7D8C]/30",
};

function StatCard({ label, value, sublabel, icon: Icon, accent, testid, onClick, active }) {
    const clickable = typeof onClick === "function";
    const activeCls = active ? "ring-2 ring-primary/60 border-primary/40" : "";
    const hoverCls = clickable ? "cursor-pointer hover:border-primary/40 hover:-translate-y-0.5 transition-all" : "";
    return (
        <button
            type={clickable ? "button" : undefined}
            onClick={onClick}
            disabled={!clickable}
            className={`tactile-card rounded-md p-6 flex flex-col gap-3 text-left w-full ${hoverCls} ${activeCls} ${clickable ? "" : "cursor-default"}`}
            data-testid={testid}
            aria-pressed={clickable ? !!active : undefined}
        >
            <div className="flex items-center justify-between w-full">
                <p className="overline text-muted-foreground">{label}</p>
                <Icon className={`w-4 h-4 ${accent}`} strokeWidth={1.5} />
            </div>
            <p className="font-display text-5xl font-bold leading-none tracking-tight">{value}</p>
            {sublabel && <p className="text-sm text-muted-foreground">{sublabel}</p>}
        </button>
    );
}

function TotalValueCard({ value, byLocation }) {
    const navigate = useNavigate();
    const [hidden, setHidden] = useState(() => {
        try { return localStorage.getItem(TOTAL_VALUE_HIDDEN_KEY) === "1"; } catch { return false; }
    });

    useEffect(() => {
        try { localStorage.setItem(TOTAL_VALUE_HIDDEN_KEY, hidden ? "1" : "0"); } catch { /* ignore */ }
    }, [hidden]);

    const chips = LOCATION_ORDER
        .map((loc) => ({ loc, amount: byLocation?.[loc] || 0 }))
        .filter((c) => c.amount > 0);

    return (
        <div
            className="tactile-card rounded-md p-6 flex flex-col gap-3 relative group"
            data-testid="stat-total-value"
        >
            <button
                type="button"
                onClick={() => setHidden(h => !h)}
                className="absolute inset-0 w-full h-full rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                aria-label={hidden ? "Show total value" : "Hide total value"}
                title={hidden ? "Click to reveal" : "Click to hide"}
                data-testid="total-value-toggle"
            />
            <div className="flex items-center justify-between relative pointer-events-none">
                <p className="overline text-muted-foreground">Total value</p>
                <span className="flex items-center gap-1.5 text-primary">
                    {hidden ? (
                        <EyeOff className="w-4 h-4" strokeWidth={1.5} data-testid="total-value-hidden-icon" />
                    ) : (
                        <PoundSterling className="w-4 h-4" strokeWidth={1.5} data-testid="total-value-shown-icon" />
                    )}
                </span>
            </div>
            <p
                className="font-display text-2xl font-bold leading-none tracking-tight min-h-[2rem] tabular-nums truncate relative pointer-events-none"
                data-testid="total-value-amount"
            >
                {hidden ? (
                    <span className="text-muted-foreground/30 tracking-widest">••••</span>
                ) : (
                    formatMoney(value || 0)
                )}
            </p>
            {!hidden && chips.length > 0 && (
                <div
                    className="flex flex-wrap gap-1 relative pointer-events-none"
                    data-testid="total-value-chips"
                >
                    {chips.map(({ loc, amount }) => (
                        <span
                            key={loc}
                            className={`px-1.5 py-0.5 rounded-full border text-[10px] font-mono tabular-nums leading-tight ${LOCATION_CHIP_COLORS[loc] || "bg-muted text-muted-foreground border-border"}`}
                            data-testid={`total-value-chip-${loc}`}
                        >
                            {loc} {formatMoneyWhole(amount)}
                        </span>
                    ))}
                </div>
            )}
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); navigate("/report"); }}
                className="relative inline-flex items-center gap-1 text-xs text-primary hover:underline w-fit z-10 pointer-events-auto"
                data-testid="total-value-report-link"
            >
                <FileBarChart2 className="w-3 h-3" strokeWidth={1.75} />
                Insurance report
            </button>
        </div>
    );
}

export default function StatsBar({ stats, activeLocation, onFilterLocation }) {
    const setLoc = (loc) => {
        if (typeof onFilterLocation !== "function") return;
        // Toggle: clicking the currently-active tile clears the filter.
        onFilterLocation(activeLocation === loc ? "all" : loc);
    };
    const resetLoc = () => onFilterLocation && onFilterLocation("all");
    return (
        <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4" data-testid="stats-bar">
            <TotalValueCard value={stats.total_value} byLocation={stats.value_by_location} />
            <StatCard
                label="Total records"
                value={stats.total}
                sublabel="Total Records"
                icon={Disc3}
                accent="text-primary"
                testid="stat-total"
                onClick={resetLoc}
                active={!activeLocation || activeLocation === "all"}
            />
            <StatCard
                label="Andy"
                value={stats.andy}
                sublabel="At Andy's"
                icon={Users}
                accent="text-[#4A5D4E]"
                testid="stat-andy"
                onClick={() => setLoc("Andy")}
                active={activeLocation === "Andy"}
            />
            <StatCard
                label="Ruth"
                value={stats.ruth}
                sublabel="At Ruth's"
                icon={Users}
                accent="text-[#C05A35]"
                testid="stat-ruth"
                onClick={() => setLoc("Ruth")}
                active={activeLocation === "Ruth"}
            />
            <StatCard
                label="Lagom"
                value={stats.lagom || 0}
                sublabel="At Studio"
                icon={Building2}
                accent="text-[#5A7D8C]"
                testid="stat-lagom"
                onClick={() => setLoc("Lagom")}
                active={activeLocation === "Lagom"}
            />
            <StatCard
                label="Archive"
                value={stats.archive || 0}
                sublabel="In storage"
                icon={ArchiveIcon}
                accent="text-foreground/60"
                testid="stat-archive"
                onClick={() => setLoc("Archive")}
                active={activeLocation === "Archive"}
            />
        </section>
    );
}
